import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Swal from 'sweetalert2';
import api from '../../services/api';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
    minHeight: 38,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

function AllocationModal({ tenantId, course, academicSessionId, onClose, onSaved }) {
  const [examTypes, setExamTypes] = useState([]);
  const [paperSets, setPaperSets] = useState([]);
  const [users, setUsers] = useState([]);
  const [existingAllocations, setExistingAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingExamType, setCreatingExamType] = useState(false);
  const [creatingSet, setCreatingSet] = useState(false);

  const [selectedExamType, setSelectedExamType] = useState(null);
  const [setterDueDate, setSetterDueDate] = useState('');
  const [moderatorDueDate, setModeratorDueDate] = useState('');
  const [sets, setSets] = useState([
    { paperSet: null, setter: null, moderator: null },
  ]);
  const [valuators, setValuators] = useState([]);

  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.UserID, label: `${u.FullName} (${u.Email || u.Username})` })),
    [users]
  );

  const examTypeOptions = useMemo(
    () => examTypes.map((e) => ({ value: e.ExamTypeID, label: e.ExamTypeName })),
    [examTypes]
  );

  const paperSetOptions = useMemo(
    () => paperSets.map((p) => ({ value: p.PaperSetID, label: p.SetName })),
    [paperSets]
  );

  const loadMasters = useCallback(async () => {
    try {
      setLoading(true);
      const [et, ps, us, existing] = await Promise.all([
        api.get(`/paper-allocations/exam-types/${tenantId}`),
        api.get(`/paper-allocations/paper-sets/${tenantId}`),
        api.get(`/paper-allocations/users/${tenantId}`),
        api.get(`/paper-allocations/by-course/${course.CourseID}/${academicSessionId}`),
      ]);
      if (et.data.success) setExamTypes(et.data.data);
      if (ps.data.success) setPaperSets(ps.data.data);
      if (us.data.success) setUsers(us.data.data);
      if (existing.data.success) setExistingAllocations(existing.data.data);
    } catch {
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  }, [tenantId, course.CourseID, academicSessionId]);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  // Pre-fill when selecting an exam type that already has an allocation
  useEffect(() => {
    if (!selectedExamType) {
      setSets([{ paperSet: null, setter: null, moderator: null }]);
      setValuators([]);
      setSetterDueDate('');
      setModeratorDueDate('');
      return;
    }
    const existing = existingAllocations.find((a) => a.ExamTypeID === selectedExamType.value);
    if (existing) {
      setSetterDueDate(existing.SetterDueDate ? existing.SetterDueDate.slice(0, 10) : '');
      setModeratorDueDate(existing.ModeratorDueDate ? existing.ModeratorDueDate.slice(0, 10) : '');
      setSets(
        (existing.sets || []).map((s) => ({
          paperSet: { value: s.PaperSetID, label: s.SetName },
          setter: { value: s.SetterUserID, label: s.SetterName },
          moderator: s.ModeratorUserID ? { value: s.ModeratorUserID, label: s.ModeratorName } : null,
        }))
      );
      setValuators(
        (existing.valuators || []).map((v) => ({ value: v.UserID, label: v.FullName }))
      );
    } else {
      setSets([{ paperSet: null, setter: null, moderator: null }]);
      setValuators([]);
      setSetterDueDate('');
      setModeratorDueDate('');
    }
  }, [selectedExamType, existingAllocations]);

  const handleCreateExamType = async (inputValue) => {
    setCreatingExamType(true);
    try {
      const res = await api.post('/paper-allocations/exam-types', { tenantId, examTypeName: inputValue.trim() });
      if (res.data.success) {
        const newItem = { ExamTypeID: res.data.data.examTypeId, ExamTypeName: res.data.data.examTypeName };
        setExamTypes((prev) => [...prev, newItem]);
        setSelectedExamType({ value: newItem.ExamTypeID, label: newItem.ExamTypeName });
        toast.success(`Exam Type "${inputValue.trim()}" created`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create Exam Type');
    } finally {
      setCreatingExamType(false);
    }
  };

  const handleCreateSet = async (inputValue, rowIdx) => {
    setCreatingSet(true);
    try {
      const res = await api.post('/paper-allocations/paper-sets', { tenantId, setName: inputValue.trim() });
      if (res.data.success) {
        const newItem = { PaperSetID: res.data.data.paperSetId, SetName: res.data.data.setName, SequenceNo: res.data.data.sequenceNo };
        setPaperSets((prev) => [...prev, newItem]);
        const opt = { value: newItem.PaperSetID, label: newItem.SetName };
        setSets((prev) => prev.map((s, i) => (i === rowIdx ? { ...s, paperSet: opt } : s)));
        toast.success(`Set "${inputValue.trim()}" created`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create Set');
    } finally {
      setCreatingSet(false);
    }
  };

  const addSetRow = () => {
    setSets((prev) => [...prev, { paperSet: null, setter: null, moderator: null }]);
  };

  const removeSetRow = (idx) => {
    setSets((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateSetRow = (idx, field, value) => {
    setSets((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedExamType) return toast.error('Please select an Exam Type');
    if (sets.length === 0) return toast.error('At least one Paper Set is required');
    for (let i = 0; i < sets.length; i++) {
      if (!sets[i].paperSet) return toast.error(`Row ${i + 1}: please select a Set`);
      if (!sets[i].setter) return toast.error(`Row ${i + 1}: please select a Setter`);
    }
    // Duplicate set check
    const setIds = sets.map((s) => s.paperSet.value);
    if (new Set(setIds).size !== setIds.length) {
      return toast.error('Duplicate Sets detected. Each Set must appear only once.');
    }

    setSaving(true);
    try {
      const payload = {
        tenantId,
        courseId: course.CourseID,
        academicSessionId,
        examTypeId: selectedExamType.value,
        setterDueDate: setterDueDate || null,
        moderatorDueDate: moderatorDueDate || null,
        sets: sets.map((s) => ({
          paperSetId: s.paperSet.value,
          setterUserId: s.setter.value,
          moderatorUserId: s.moderator?.value || null,
        })),
        valuators: valuators.map((v) => ({ userId: v.value })),
      };

      await api.post('/paper-allocations', payload);
      toast.success('Allocation saved');
      onSaved();
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message || 'Failed to save allocation';
      if (status === 409) {
        // Business-rule conflict (e.g. paper already started for this set) —
        // surface as a clear SweetAlert the user can't miss.
        await Swal.fire({
          title: 'Allocation cannot be changed',
          text: message,
          icon: 'warning',
          confirmButtonColor: '#1a237e',
          confirmButtonText: 'OK',
        });
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg alloc-modal" style={{ maxWidth: 900, maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>Allocate Paper Users</h2>
            <div style={{ marginTop: 4, fontSize: 13, color: '#616161' }}>
              <strong>{course.CourseCode}</strong> &middot; {course.CourseName}
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Exam Type */}
            <div className="form-group">
              <label className="required-label">Exam Type</label>
              <CreatableSelect
                options={examTypeOptions}
                value={selectedExamType}
                onChange={setSelectedExamType}
                onCreateOption={handleCreateExamType}
                placeholder="Select or create (e.g., Continuous Assessment, Final Exam)"
                isClearable
                isSearchable
                isLoading={creatingExamType}
                styles={selectStyles}
                formatCreateLabel={(input) => `Create "${input}"`}
              />
              {selectedExamType && existingAllocations.find((a) => a.ExamTypeID === selectedExamType.value) && (
                <span className="form-hint" style={{ color: '#00796b' }}>
                  Existing allocation loaded for this Exam Type. Submit will overwrite.
                </span>
              )}
            </div>

            {/* Due Dates */}
            <div style={{ display: 'flex', gap: 16 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Due Date for Setter</label>
                <input type="date" value={setterDueDate} onChange={(e) => setSetterDueDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Due Date for Moderation</label>
                <input type="date" value={moderatorDueDate} onChange={(e) => setModeratorDueDate(e.target.value)} />
              </div>
            </div>

            {/* Sets */}
            <div className="form-group">
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="required-label" style={{ margin: 0 }}>Paper Sets (Setter &amp; Moderator)</span>
                <button type="button" className="btn btn-outline btn-sm" onClick={addSetRow}>+ Add Set</button>
              </label>
              <div className="alloc-sets-list">
                {sets.map((row, idx) => (
                  <div key={idx} className="alloc-set-row">
                    <div style={{ flex: 1 }}>
                      <CreatableSelect
                        options={paperSetOptions}
                        value={row.paperSet}
                        onChange={(v) => updateSetRow(idx, 'paperSet', v)}
                        onCreateOption={(val) => handleCreateSet(val, idx)}
                        placeholder="Set"
                        isClearable
                        isLoading={creatingSet}
                        styles={selectStyles}
                        formatCreateLabel={(input) => `Create "${input}"`}
                      />
                    </div>
                    <div style={{ flex: 2 }}>
                      <Select
                        options={userOptions}
                        value={row.setter}
                        onChange={(v) => updateSetRow(idx, 'setter', v)}
                        placeholder="Setter"
                        isClearable
                        isSearchable
                        styles={selectStyles}
                      />
                    </div>
                    <div style={{ flex: 2 }}>
                      <Select
                        options={userOptions}
                        value={row.moderator}
                        onChange={(v) => updateSetRow(idx, 'moderator', v)}
                        placeholder="Moderator (optional)"
                        isClearable
                        isSearchable
                        styles={selectStyles}
                      />
                    </div>
                    <button
                      type="button"
                      className="action-btn action-delete"
                      title="Remove"
                      onClick={() => removeSetRow(idx)}
                      disabled={sets.length === 1}
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Valuators panel */}
            <div className="form-group">
              <label>Valuators Panel (independent of Paper Set)</label>
              <Select
                isMulti
                options={userOptions}
                value={valuators}
                onChange={(v) => setValuators(v || [])}
                placeholder="Select one or more users..."
                isSearchable
                styles={selectStyles}
                closeMenuOnSelect={false}
              />
              <span className="form-hint">Add multiple valuators. Each will see assigned papers during valuation.</span>
            </div>

            <div className="form-actions" style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
              <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-save" disabled={saving}>
                {saving ? 'Saving...' : 'Save Allocation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default AllocationModal;
